import { useFormik } from 'formik'
import { useDispatch, useSelector } from 'react-redux'
import { useCallback, useMemo } from 'react'

import { usePurifiedFormik } from '../../../../../../hooks/formHooks/usePurifiedFormik'
import { formatPercentageAllocationLineItems } from '../formatters/percentageAllocationHelpers/formatPercentageAllocationLineItems'
import { getAllocationInfo } from '../../../../../Multiplatform/Partnerships/RightForms/RightCreate/RightCreateForm/sectionsLists/OnsiteRightCreateSections/GamOnsiteRightCreateSections/helpers'
import { formatQuantityAllocationLineItems } from '../formatters/quantityAllocationHelpers/formatQuantityAllocationLineItems'
import { useRemainingBudgetValidation, useSubAllocationRemainingBudget } from './remainingBudgetHooks'

import { createBulkGamLineItems } from '../../../../../../modules/actions/googleAdManager'
import { lineItemsSelector } from '../../../../../../modules/selectors/lineItems'
import { selfAccountSelector } from '../../../../../../modules/selectors/selfAccounts'
import { selectedPartnershipSelector } from '../../../../../../modules/selectors/partnerships'
import { gamRightUploadAdDataSelector, rightSelector } from '../../../../../../modules/selectors/rights'

import { LIST_ITEM_OPTION, OPTION_EXISTING_LIST_ITEM } from '../../../../../../hooks/formHooks/listItemFormHooks/fields'
import { LINE_ITEM } from '../../../../../Multiplatform/UploadAdCreative/UploadAdCreativeLineItemForm/fields'

import { GAM_NETWORK_CODE } from '../../../../../Multiplatform/Partnerships/RightForms/RightCreate/RightCreateForm/sectionsLists/OnsiteRightCreateSections/GamOnsiteRightCreateSections/fields'
import { GOOGLE_AD_MANAGER_AD_ACCOUNT } from '../../../../../Multiplatform/Partnerships/RightForms/RightCreate/RightCreateForm/sectionsLists/OnsiteRightCreateSections/fields'
import { getInitialValues } from '../fields'
import { getValidationSchema } from '../validation'

const useHandleFormik = ({ validationState, handleExistedLineItemSubmit }) => {
  const dispatch = useDispatch()

  // line items are loaded when submitting previous CampaignForm with existing campaign option
  const lineItems = useSelector(lineItemsSelector)
  const selfAccount = useSelector(selfAccountSelector)
  const gamNetworkCode = selfAccount?.[GAM_NETWORK_CODE]
  const right = useSelector(rightSelector)
  const { campaign } = useSelector(gamRightUploadAdDataSelector)
  const campaignId = campaign?.id
  const selectedPartnership = useSelector(selectedPartnershipSelector)
  const currency = selectedPartnership[GOOGLE_AD_MANAGER_AD_ACCOUNT].currency
  const { isPercentageAllocation } = getAllocationInfo(right)
  const { remainingBudget } = useRemainingBudgetValidation()
  const inventoryBudgets = useSubAllocationRemainingBudget()

  const createBulkLineItemsHandler = useCallback(
    formattedLineItems => {
      const bulkData = {
        network_code: gamNetworkCode,
        line_items: formattedLineItems
      }

      dispatch(createBulkGamLineItems(bulkData))
    },
    [dispatch, gamNetworkCode]
  )

  const handleNewLineItemSubmit = useCallback(
    values => {
      // Check current validation state from the form
      if (validationState && !validationState.isValid && validationState.errors.length > 0) {
        console.error('GAM targeting validation failed - form submission blocked:', validationState.errors)
        // stop the submit process
        return
      }

      // If GAM targeting validation passes, proceed with normal submission
      const formattedLineItems = isPercentageAllocation
        ? formatPercentageAllocationLineItems({
            values,
            right,
            campaignId,
            currency
          })
        : formatQuantityAllocationLineItems({
            values,
            right,
            campaignId,
            currency
          })

      createBulkLineItemsHandler(formattedLineItems)
    },
    [createBulkLineItemsHandler, isPercentageAllocation, right, campaignId, currency, validationState]
  )

  const onSubmit = useCallback(
    values => {
      if (values[LIST_ITEM_OPTION] === OPTION_EXISTING_LIST_ITEM) {
        handleExistedLineItemSubmit(values[LINE_ITEM])
      } else {
        handleNewLineItemSubmit(values)
      }
    },
    [handleExistedLineItemSubmit, handleNewLineItemSubmit]
  )

  const isLineItemsExist = useMemo(() => {
    // todo PBR-1308 - remove this filtering when fetch lineItems inventory overview separately from lineItems
    const lineItemsFilteredByCampaignId = lineItems.filter(lineItem => lineItem.order_id === campaignId)
    return !!lineItemsFilteredByCampaignId.length
  }, [lineItems, campaignId])

  const initialValues = useMemo(
    () => getInitialValues({ isLineItemsExist, isPercentageAllocation }),
    [isLineItemsExist, isPercentageAllocation]
  )
  const validationSchema = useMemo(
    () => getValidationSchema(right, remainingBudget, inventoryBudgets),
    [right, remainingBudget, inventoryBudgets]
  )
  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit
  })

  return usePurifiedFormik(formik)
}

export default useHandleFormik
